import React from 'react'
import { Link } from 'react-router-dom'
import { Image } from '@deal/components'
import { getSellableUrl } from '#src/services/sellable'
import { GroupedLineItemsType } from '#src/services/cart'
import defaultProduct from '#src/assets/images/default-product.png'
import { OrderLineItemFragment } from '../../../../../fragments/OrderLineItem.generated'
import { TrackableSellableFragment } from '../../../../../fragments/TrackableSellable.generated'
import ProductLineItemPrice from '../ProductLineItemPrice'
import PreOrderInfo from '../../../../PreOrderInfo'
import CartAlerts from '../CartAlerts'
import MinusIcon from './minus.svg'
import PlusIcon from './plus.svg'
import RemoveIcon from './remove.svg'
import styles from './styles.css'

interface Props {
  group: GroupedLineItemsType
  onRemoveItem: (lineItemId: string, sellable?: TrackableSellableFragment) => void
  onUpdateQuantity: (lineItemId: string, quantity: number) => void
  loadingUpdateQuantity: boolean
}

class ProductLineItemGroup extends React.Component<Props> {
  public isUrgent(lineItem: OrderLineItemFragment) {
    return (
      lineItem.source.__typename === 'OrderLineItemSellableSource' &&
      lineItem.source.sellable.inShortSupply
    )
  }
  public getLineItemImage(lineItem: OrderLineItemFragment) {
    // Going to leave this if check for now since this will probably behave a little
    // different with adHoc and expert item sources
    if (lineItem.source.__typename !== 'OrderLineItemSellableSource') {
      return defaultProduct
    }

    if (lineItem.source.sellable && lineItem.source.sellable.images.length > 0) {
      return lineItem.source.sellable.images[0].url
    } else {
      return defaultProduct
    }
  }

  public getLineItemSellable(lineItem: OrderLineItemFragment) {
    if (lineItem.source.__typename === 'OrderLineItemSellableSource') {
      return lineItem.source.sellable
    }

    return undefined
  }

  public getLineItemSellableVariants(lineItem: OrderLineItemFragment) {
    const sellable = this.getLineItemSellable(lineItem)
    if (!sellable) {
      return null
    }

    return sellable.variants
  }

  public render() {
    const { group } = this.props
    const { parentLineItem } = group

    const lineItemImage = this.getLineItemImage(parentLineItem)
    const sellable = this.getLineItemSellable(group.parentLineItem)
    const sellableVariants = this.getLineItemSellableVariants(group.parentLineItem)
    const originalPrice = sellable && sellable.originalPrice ? sellable.originalPrice.amount : null
    const showPreOrderInfo = sellable && sellable.availableForPreOrder

    return (
      <>
        <div className={styles.lineItem}>
          <div className={styles.imageContainer}>
            {sellable && (
              <Link to={getSellableUrl(sellable)} className={styles.link}>
                <Image
                  src={lineItemImage}
                  className={styles.image}
                  width={80}
                  imgixParams={{ fit: 'clip' }}
                />
              </Link>
            )}
            {!sellable && (
              <Image
                src={lineItemImage}
                className={styles.image}
                width={80}
                imgixParams={{ fit: 'clip' }}
              />
            )}
          </div>
          <div className={styles.details}>
            {sellable && (
              <Link
                to={getSellableUrl(sellable)}
                className={styles.title}
                data-testid="cart-item-title"
              >
                {parentLineItem.title}
              </Link>
            )}
            {!sellable && <span className={styles.title}>{parentLineItem.title}</span>}

            {sellableVariants && (
              <div className={styles.variants}>
                {sellableVariants.map((variant, idx) => (
                  <span key={variant.displayValueHtml}>
                    {variant.displayValueHtml}
                    {idx === sellableVariants.length - 1 ? '' : ', '}
                  </span>
                ))}
              </div>
            )}

            {/* If there are no children we just render the price for parent sellable, otherwise we map over all grouped children line items*/}
            {group.children.length === 0 ? (
              <ProductLineItemPrice lineItem={group.parentLineItem} originalPrice={originalPrice} />
            ) : (
              group.children.map(child => (
                <ProductLineItemPrice
                  key={child.id}
                  lineItem={child}
                  originalPrice={originalPrice}
                />
              ))
            )}

            <div className={styles.quantityContainer}>
              <div className={styles.incrementDecrement}>
                <button
                  className={styles.quantityButton}
                  disabled={parentLineItem.quantity < 2 || this.props.loadingUpdateQuantity}
                  onClick={() => {
                    this.props.onUpdateQuantity(parentLineItem.id, parentLineItem.quantity - 1)
                  }}
                >
                  <MinusIcon />
                </button>
                <div className={styles.quantityNumber}>{parentLineItem.quantity}</div>
                <button
                  className={styles.quantityButton}
                  onClick={() => {
                    this.props.onUpdateQuantity(parentLineItem.id, parentLineItem.quantity + 1)
                  }}
                  disabled={this.props.loadingUpdateQuantity}
                >
                  <PlusIcon />
                </button>
              </div>
              <button
                className={styles.removeButton}
                onClick={() => {
                  this.props.onRemoveItem(
                    parentLineItem.id,
                    this.getLineItemSellable(parentLineItem)
                  )
                }}
              >
                <RemoveIcon />
              </button>
            </div>

            {showPreOrderInfo && (
              <PreOrderInfo
                availableAt={sellable?.availableAt}
                wrapperClassName={styles.preorderWrapper}
              />
            )}
          </div>
        </div>
        <CartAlerts messages={parentLineItem.messages} />
      </>
    )
  }
}

export default ProductLineItemGroup
